import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import * as TestDefinitionsService from '../services/TestDefinitionsService';
import {
    TestDefinitionFormValues,
    TestDefinitionStatus,
} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';
import {testingApiAvailable} from '../../testing-api-integration/config';

// Query keys
export const testDefinitionsKeys = {
    all: ['testing-test-definitions'] as const,
    lists: () => [...testDefinitionsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...testDefinitionsKeys.lists(), {filters}] as const,
    details: () => [...testDefinitionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...testDefinitionsKeys.details(), id] as const,
};

export const useTestDefinitionsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
                        queryKey: testDefinitionsKeys.list(filters),
                        queryFn: () => TestDefinitionsService.fetchRecords(filters),
                        enabled: testingApiAvailable,
                    });
};

export const useTestDefinitionQuery = (id: string) => {
    return useQuery({
                        queryKey: testDefinitionsKeys.detail(id),
                        queryFn: () => TestDefinitionsService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

export const useCreateTestDefinitionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: TestDefinitionsService.add,
                           onSuccess: () => {
                               notifySuccess('Test definition created');
                               queryClient.invalidateQueries({queryKey: testDefinitionsKeys.lists()});
                           },
                           onError: notifyError,
                       });
};

export const useUpdateTestDefinitionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: ({
                                            id,
                                            record
                                        }: { id: string; record: TestDefinitionFormValues }) =>
                               TestDefinitionsService.update(id, record),
                           onSuccess: (_, {id}) => {
                               notifySuccess('Test definition updated');
                               queryClient.invalidateQueries({queryKey: testDefinitionsKeys.lists()});
                               queryClient.invalidateQueries({queryKey: testDefinitionsKeys.detail(id)});
                           },
                           onError: notifyError,
                       });
};

export const useDeleteTestDefinitionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: TestDefinitionsService.deleteRecord,
                           onSuccess: () => {
                               notifySuccess('Test definition deleted');
                               queryClient.invalidateQueries({queryKey: testDefinitionsKeys.all});
                           },
                           onError: notifyError,
                       });
};

// Status transitions are a CONDITIONAL WRITE on the server. An illegal transition
// (e.g. RETIRED -> ACTIVE) fails, and that failure is surfaced to the user via
// notifyError rather than swallowed — the operator needs to see it.
export const useSetTestDefinitionStatusMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: ({
                                            id,
                                            targetStatus
                                        }: { id: string; targetStatus: TestDefinitionStatus }) =>
                               TestDefinitionsService.setStatus(id, targetStatus),
                           onSuccess: (record, {targetStatus}) => {
                               notifySuccess(`Test definition status set to ${targetStatus}`);
                               queryClient.invalidateQueries({queryKey: testDefinitionsKeys.lists()});
                               if (record?.id) {
                                   queryClient.invalidateQueries({queryKey: testDefinitionsKeys.detail(record.id)});
                               }
                           },
                           onError: notifyError,
                       });
};
